import { z } from "zod";

import { reportSchemaError } from "@/diagnostics/schema-error.ts";
import { DistrictSetSchema } from "@/districts/district.model.ts";
import type { LngLat } from "@/map/geo.ts";

/**
 * Everything the app remembers between visits, in one object. It round-trips
 * through localStorage under a versioned key and is parsed back through zod, so
 * a hand-edited or stale blob degrades to defaults instead of white-screening.
 */

const STORAGE_KEY = "radar.state.v1";

/**
 * Age slider stops. Discrete rather than a linear sweep because the useful
 * resolution is all at the fresh end — the difference between 1 and 2 days
 * matters, the difference between 60 and 61 does not.
 *
 * It stops at a fortnight, and there is no "any age" position. The stops beyond
 * it were a scale with nothing on it worth choosing: in this market a listing
 * that has been up three weeks is gone, so those positions only ever answered a
 * question nobody was asking while making every useful stop harder to hit. The
 * end of the slider is also the end of the colour ramp (see AGE_SCALE), so every
 * colour the map can draw is one the filter can reach.
 */
export const AGE_STOPS = [1, 2, 3, 5, 7, 10, 14] as const;
export const AGE_MAX = AGE_STOPS[AGE_STOPS.length - 1];

/**
 * Snap a persisted age onto the stops.
 *
 * Anything saved before the fortnight cap — including the old "any age"
 * sentinel of ~10 years — would otherwise leave the slider pinned at one end
 * while the filter went on admitting listings from months ago, so the readout
 * and the map would disagree with nothing on screen to explain why.
 */
export function snapAge(days: number): number {
  return AGE_STOPS.find((s) => s >= days) ?? AGE_MAX;
}

/** "24 H" / "5 D" — the panel renders the two words at different sizes, so
 *  every label has to be exactly `value unit`. */
export function ageStopLabel(days: number): string {
  return days === 1 ? "24 H" : `${days} D`;
}

/**
 * Budget slider stops, shared by both thumbs. Clustered where the SF market
 * actually sits — the gap between $2,500 and $3,000 decides far more searches
 * than the one between $9,000 and $10,000. The first stop is the "no floor"
 * sentinel and the last is the "no cap" sentinel; $1,000 exists mostly as the
 * floor that clears out the too-cheap-to-be-real tail.
 */
export const PRICE_STOPS = [
  0, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 6000, 8000, 10000, 1_000_000,
] as const;
export const PRICE_ANY = 1_000_000;
export const PRICE_NO_FLOOR = 0;

/** "$2.5k" / "ANY" — split on the space by the panel's two-size readout. */
export function priceStopLabel(price: number): string {
  if (price >= PRICE_ANY) return "ANY";
  if (price <= PRICE_NO_FLOOR) return "$0";
  const k = price / 1000;
  return `$${k % 1 === 0 ? k : k.toFixed(1)} k`;
}

/**
 * Size slider stops, in square feet. A *minimum*, unlike every other slider —
 * nobody filters out an apartment for being too big — so the scale runs from
 * "any size" at the left up through the floors worth setting. Clustered through
 * the studio/1BR band where SF actually decides, thinning past 1,000 where the
 * market does. The first stop is the "no minimum" sentinel.
 */
export const SQFT_STOPS = [0, 300, 400, 500, 600, 700, 800, 1000, 1250, 1500, 2000] as const;
export const SQFT_NONE = 0;

/** "600 ft²" / "ANY" — two tokens, like the other stop labels. */
export function sqftStopLabel(sqft: number): string {
  if (sqft <= SQFT_NONE) return "ANY";
  return `${sqft.toLocaleString("en-US")} ft²`;
}

/**
 * Travel-time slider stops, in minutes. Tight where a commute decision is
 * actually made — 15 vs 20 minutes changes which places you'd tour, 80 vs 90
 * does not. The last stop is the "no limit" sentinel.
 */
export const TRAVEL_STOPS = [10, 15, 20, 25, 30, 40, 50, 60, 90, 6000] as const;
export const TRAVEL_ANY = 6000;

/** "25 MIN" / "1 HR" / "ANY" — two tokens, like the other stop labels. */
export function travelStopLabel(mins: number): string {
  if (mins >= TRAVEL_ANY) return "ANY";
  if (mins < 60) return `${mins} MIN`;
  const hours = mins / 60;
  return `${hours % 1 === 0 ? hours : hours.toFixed(1)} HR`;
}

/**
 * Which mode the travel limit is measured by. "any" takes whichever mode is
 * fastest for that listing — the honest reading of "I need to get there in
 * 30 minutes", where how you do it is your business.
 *
 * The three named modes mirror `travel.model.ts`'s TravelMode; a divergence is a
 * type error where this feeds `travelMinutesFor`.
 */
export type TravelFilterMode = z.infer<typeof TravelFilterModeSchema>;
export const TravelFilterModeSchema = z.enum(["any", "walk", "scooter", "transit"]);

export const TRAVEL_MODE_LABELS: Record<TravelFilterMode, string> = {
  any: "Any",
  walk: "Walk",
  scooter: "Scoot",
  transit: "Transit",
};

const LngLatSchema = z.tuple([z.number(), z.number()]);

/** Which slice of the map is in play. The three modes are mutually exclusive
 *  by construction — one discriminated union, one active area. */
export type Area = z.infer<typeof AreaSchema>;
export const AreaSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("all") }),
  z.object({ mode: z.literal("radius"), center: LngLatSchema, radiusM: z.number().positive() }),
  z.object({ mode: z.literal("districts"), ids: z.array(z.string()).min(1) }),
]);

export type AppState = z.infer<typeof AppStateSchema>;
export const AppStateSchema = z.object({
  /** Hide anything posted more than this many days ago. */
  maxAgeDays: z.number().positive(),
  /** Bed buckets to show; 3 means "3 or more". */
  beds: z.array(z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)])),
  /** Keep listings whose source never reported a bed count. */
  includeUnknownBeds: z.boolean(),
  /** Hide anything asking more than this per month. PRICE_ANY = no cap. */
  maxPrice: z.number().positive(),
  /** Hide anything asking less than this per month — the "$900 studio that
   *  cannot be real" filter. PRICE_NO_FLOOR (0) = no floor. */
  minPrice: z.number().nonnegative(),
  /** Hide anything smaller than this. SQFT_NONE (0) = any size. */
  minSqft: z.number().nonnegative(),
  /** Keep listings whose source never reported a size, while a floor is set. */
  includeUnknownSqft: z.boolean(),
  /** Hide anything further than this from the anchor. TRAVEL_ANY = no cap. */
  maxTravelMin: z.number().positive(),
  /** Which mode `maxTravelMin` is measured by. */
  travelMode: TravelFilterModeSchema,
  area: AreaSchema,
  /** Group nearby listings into counted bubbles when zoomed out. */
  clusterListings: z.boolean(),
  /** Put locally-hidden listings back on the map (see listing.hidden.ts). */
  showHidden: z.boolean(),
  /** Overlay streets with dedicated bike infrastructure. */
  showBikeways: z.boolean(),
  /** Which polygon set the district picker offers. */
  districtSet: DistrictSetSchema,
  camera: z.object({ center: LngLatSchema, zoom: z.number() }),
  /** Desktop only — the mobile drawer always starts closed. */
  panelOpen: z.boolean(),
});

/** Centred on SoMa, tight enough that the first paint is already useful. */
export const SF_CENTER: LngLat = [-122.4194, 37.7749];

export const DEFAULT_STATE: AppState = {
  // Three days, not a week: in this market a listing that has been up longer is
  // usually already gone, so the default view is the one worth acting on.
  maxAgeDays: 3,
  // Only Studio and 1BR to start, per the common case: one person, one bedroom.
  beds: [0, 1],
  includeUnknownBeds: false,
  maxPrice: 4500,
  minPrice: PRICE_NO_FLOOR,
  minSqft: SQFT_NONE,
  // Included by default: sqft coverage is patchy, and a size floor that
  // silently dropped every unmeasured unit would punish honest gaps in the
  // data rather than small apartments.
  includeUnknownSqft: true,
  // Twenty minutes by any means you like — a commute you would actually accept,
  // and the mode left open because how you get there is your business.
  maxTravelMin: 20,
  travelMode: "any",
  area: { mode: "all" },
  clusterListings: true,
  showHidden: false,
  // On by default: whether a street is survivable on a scooter changes which
  // listings are worth touring, which makes this closer to data than decoration.
  showBikeways: true,
  districtSet: "neighborhood",
  camera: { center: SF_CENTER, zoom: 12.2 },
  panelOpen: true,
};

/**
 * What we accept back out of localStorage. Every key is optional: a blob saved
 * by an earlier build simply predates whichever field was added since, and
 * filling that from defaults is normal, not an error. A key that IS present but
 * has the wrong shape still fails — that's corruption worth shouting about.
 */
const PersistedStateSchema = AppStateSchema.partial();

export function loadState(): AppState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = PersistedStateSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) {
      // Recoverable — we fall back to defaults — but never silent: a blob that
      // fails even the lenient schema means someone's saved view was thrown away.
      reportSchemaError("AppState", parsed.error);
      return DEFAULT_STATE;
    }
    const merged = { ...DEFAULT_STATE, ...parsed.data };
    return { ...merged, maxAgeDays: snapAge(merged.maxAgeDays) };
  } catch (err) {
    if (localStorage.getItem(STORAGE_KEY)) reportSchemaError("AppState", err);
    return DEFAULT_STATE;
  }
}

export function saveState(state: AppState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private-browsing quota errors are not worth interrupting the session for.
  }
}

/** True when any filter is narrowing the map, i.e. "Clear all" would do something. */
export function isFiltered(state: AppState): boolean {
  return (
    state.area.mode !== "all" ||
    state.maxAgeDays !== DEFAULT_STATE.maxAgeDays ||
    state.maxPrice !== DEFAULT_STATE.maxPrice ||
    state.minPrice !== DEFAULT_STATE.minPrice ||
    state.minSqft !== DEFAULT_STATE.minSqft ||
    state.includeUnknownSqft !== DEFAULT_STATE.includeUnknownSqft ||
    state.maxTravelMin !== DEFAULT_STATE.maxTravelMin ||
    state.beds.length !== DEFAULT_STATE.beds.length ||
    !DEFAULT_STATE.beds.every((b) => state.beds.includes(b)) ||
    state.includeUnknownBeds !== DEFAULT_STATE.includeUnknownBeds
  );
}
